<template>
  <Loader v-if="isLoading" />
  <div v-else-if="currentSlide && filteredSlides.length > 0" class="playlist-player">
    <div
      v-for="(slide, index) in filteredSlides"
      class="playlist-slide"
      :style="{ 'z-index': displaySlide(slide) ? 10 : 0 }"
      :class="{
        [slideClassIn]: displaySlide(slide),
        [slideClassOut]: !displaySlide(slide),
      }"
      :key="index"
    >
      <PlaylistSlide
        v-if="showNormalSlide(index)"
        :key="`${slide.item_id}-${slide.assoc_id}-${index}`"
        :show="slide.item_id === currentSlide.item_id"
        :slide="{
          ...(slide || filteredSlides[nextSlideIndex]),
          imageMode: currentSlide.imageMode || playlist.imageMode || 'cover',
        }"
        :style="{
          visibility: displaySlide(slide) ? 'visible' : 'hidden',
        }"
        :playlist="playlist"
      />
      <!-- Preloading videos in the background to enable seamless playback across multiple videos -->
      <VideoSlide
        v-if="
          !isAndroid &&
          !isTizen4and5and6 &&
          isSlideOfType(slide, 'video') &&
          showVideoOrAudioSlide(index, 'video')
        "
        :key="`${slide.item_id}-${slide.assoc_id}-${index}`"
        :show="slide.item_id === currentSlide.item_id"
        :restartSignal="restartSignal"
        :itemId="slide.item_id"
        :itemType="slide.item_type"
        :itemUrl="slide.item_url"
        :imageMode="slide.imageMode ?? 'cover'"
        :thumbnail="slide.thumbnail_url"
      />

      <GenericVideoPlayer
        v-if="isAndroid && isSlideOfType(slide, 'video') && showAndroidVideoSlide(index)"
        :key="`${slide.item_id}-${slide.assoc_id}-${index}`"
        :show="slide.item_id === currentSlide.item_id"
        :itemId="slide.item_id"
        :itemType="slide.item_type"
        :itemUrl="slide.item_url"
        :imageMode="slide.imageMode ?? 'cover'"
        :thumbnail="slide.thumbnail_url"
        :mute="slide.mute ?? false"
      />

      <TizenVideoPlayer
        v-if="isTizen4and5and6 && isSlideOfType(slide, 'video') && showTizenVideoSlide(index)"
        :key="`${slide.item_id}-${slide.assoc_id}-${index}`"
        :itemId="slide.item_id"
        :itemType="slide.item_type"
        :itemUrl="slide.item_url"
        :imageMode="slide.imageMode ?? 'cover'"
        :thumbnail="slide.thumbnail_url"
        :zIndex="displaySlide(slide) ? 10 : 0"
      />

      <!-- WebOS screens can only play sound from one audio tag at a time so we dont preload audio  -->
      <AudioSlide
        v-if="currentSlideItemType === 'audio' && index === currentSlideIndex"
        :key="`${slide.item_id}-${slide.assoc_id}-${index}`"
        :show="slide.item_id === currentSlide.item_id"
        :restartSignal="restartSignal"
        :itemId="slide.item_id"
        :itemType="slide.item_type"
        :itemUrl="slide.item_url"
        :imageMode="slide.imageMode ?? 'cover'"
        :mute="slide.mute ?? false"
        :volume="slide.volume ?? 1"
      />
    </div>
  </div>
  <!-- Empty state  -->
  <div v-else />
</template>

<script>
  import moment from 'moment';

  import PlaylistSlide from '@/components/player/PlaylistSlide.vue';
  import Loader from '@/components/common/Loader2.vue';
  import VideoSlide from '@/components/player/layerContent/VideoSlide.vue';
  import AudioSlide from '@/components/player/layerContent/AudioSlide.vue';
  import GenericVideoPlayer from '@/components/playerWidgets/GenericVideoPlayer.vue';
  import TizenVideoPlayer from '@/components/playerWidgets/TizenVideoPlayer.vue';
  import { DAYS_OF_THE_WEEK, DISPLAY_PRIORITY } from '@/constants';
  import { GET_PLAYLIST_ITEMS } from '@/store/actions/playlist';
  import { getTizenVersion } from '@/utils/tizen';

  import { simpleTypeMixin } from '@/helpers';
  import { WIDGET_TYPES } from '@/models/layoutDesigner';
  import { logger } from '@/utils/screenEventsLogger';

  export default {
    name: 'PlaylistPlayer',

    mixins: [simpleTypeMixin],

    props: {
      playlist: {
        type: Object,
        default: () => null,
      },
      assocId: {
        type: Number,
      },
    },

    components: {
      AudioSlide,
      VideoSlide,
      PlaylistSlide,
      Loader,
      GenericVideoPlayer,
      TizenVideoPlayer,
    },

    data() {
      return {
        currentSlideIndex: 0,
        nextSlideIndex: 0,
        time: null,
        schedulerTime: null,
        filteredSlides: [],
        restartSignal: null,
        isLoading: false,
      };
    },

    async mounted() {
      if (!this.playlist || !this.playlist.playlist_id) return;

      this.setPlaylist();

      this.schedulerTime = setInterval(this.filterPlaylistsBySchedulerAndPriority, 10 * 1000);
    },

    beforeDestroy() {
      clearInterval(this.time);
      clearInterval(this.schedulerTime);
    },

    computed: {
      playlistId() {
        return this.playlist ? this.playlist.playlist_id : null;
      },

      showNextSlide() {
        return this.filteredSlides.length > 1;
      },

      currentSlide() {
        return this.filteredSlides[this.currentSlideIndex] || this.filteredSlides[0];
      },

      currentSlideItemType() {
        if (this.filteredSlides[this.currentSlideIndex]?.item_type.includes(WIDGET_TYPES.VIDEO)) {
          return WIDGET_TYPES.VIDEO;
        } else if (
          this.filteredSlides[this.currentSlideIndex]?.item_type.includes(WIDGET_TYPES.AUDIO)
        ) {
          return WIDGET_TYPES.AUDIO;
        } else {
          return this.filteredSlides[this.currentSlideIndex]?.item_type;
        }
      },

      nextSlideItemType() {
        if (this.filteredSlides[this.nextSlideIndex]?.item_type.includes(WIDGET_TYPES.VIDEO)) {
          return WIDGET_TYPES.VIDEO;
        } else if (
          this.filteredSlides[this.nextSlideIndex]?.item_type.includes(WIDGET_TYPES.AUDIO)
        ) {
          return WIDGET_TYPES.AUDIO;
        } else {
          return this.filteredSlides[this.nextSlideIndex]?.item_type;
        }
      },

      slides() {
        return this.$store.getters.getPlaylistItems(this.assocId);
      },

      videoSlides() {
        return this.filteredSlides.filter(
          (item) => item.item_type && item.item_type.includes(WIDGET_TYPES.VIDEO),
        );
      },

      isAndroid() {
        return !!window.isAndroid;
      },

      isTizen() {
        return !!window.isTizen;
      },

      isTizen4and5and6() {
        const tizen_version = getTizenVersion();
        return this.isTizen && (isNaN(tizen_version) || tizen_version < 6.5);
      },

      slideClassIn() {
        // Note: template literals are not guarantied to work on older browsers such as webOS 4
        return this.playlist.transition_mode + 'In';
      },

      slideClassOut() {
        // Note: template literals are not guarantied to work on older browsers such as webOS 4
        return this.playlist.transition_mode + 'Out';
      },
    },

    watch: {
      filteredSlides(newFilteredSlides, oldFilteredSlides) {
        if (!this.areSlidesEqual(newFilteredSlides, oldFilteredSlides)) {
          this.currentSlideIndex = 0;
          this.nextSlideIndex = 1;
        }
      },
    },

    methods: {
      getSlideItemType(index) {
        const slide = this.filteredSlides[index];
        if (!slide) return null;
        if (slide.item_type?.includes(WIDGET_TYPES.VIDEO)) {
          return WIDGET_TYPES.VIDEO;
        } else if (slide.item_type?.includes(WIDGET_TYPES.AUDIO)) {
          return WIDGET_TYPES.AUDIO;
        }
        return slide.item_type;
      },

      showNormalSlide(index) {
        const isCurrentSlide = index === this.currentSlideIndex;
        const isNextSlide = this.showNextSlide && index === this.nextSlideIndex;

        if (!isCurrentSlide && !isNextSlide) return false;

        const slideType = this.getSlideItemType(index);
        const isNotVideoOrAudioSlide = !(
          slideType === WIDGET_TYPES.VIDEO || slideType === WIDGET_TYPES.AUDIO
        );

        return isNotVideoOrAudioSlide;
      },

      showAndroidVideoSlide(index) {
        const isCurrentSlide = index === this.currentSlideIndex;
        const isNextSlide = this.showNextSlide && index === this.nextSlideIndex;

        if (!isCurrentSlide && !isNextSlide) return false;

        const slideType = this.getSlideItemType(index);
        return slideType === WIDGET_TYPES.VIDEO;
      },

      showTizenVideoSlide(index) {
        const isVideoSlide = this.currentSlideItemType === WIDGET_TYPES.VIDEO;
        return isVideoSlide && index === this.currentSlideIndex;
      },

      showVideoOrAudioSlide(index, type) {
        const isCurrentSlide = index === this.currentSlideIndex;
        const isNextSlide = this.showNextSlide && index === this.nextSlideIndex;

        if (!isCurrentSlide && !isNextSlide) return false;

        const slideType = this.getSlideItemType(index);
        return slideType === type;
      },

      isSlideOfType(slide, type) {
        const slideItemType = slide?.item_type;
        return slideItemType && slideItemType.includes(type);
      },

      areSlidesEqual(slides1, slides2) {
        if (slides1.length !== slides2.length) {
          return false;
        }
        for (var i = 0; i < slides1.length; i++) {
          if (slides1[i].assoc_id !== slides2[i].assoc_id) {
            return false;
          }
        }
        return true;
      },

      async getPlaylistData() {
        try {
          if (!!this.slides) {
            return;
          }

          this.isLoading = true;

          await this.$store.dispatch(GET_PLAYLIST_ITEMS, {
            playlistId: this.playlistId,
            assocId: this.assocId,
          });
        } catch (error) {
          logger.addLog('error: ' + error);
        }

        this.isLoading = false;
      },

      async setPlaylist() {
        await this.getPlaylistData();

        this.filterPlaylistsBySchedulerAndPriority();
        this.currentSlideIndex = 0;

        if (this.slides?.length > 0) {
          await this.initPlaylist();
        }
      },

      filterPlaylistsBySchedulerAndPriority() {
        const tempFilteredSlides = (this.slides || []).filter((slide) => {
          if (!slide.scheduler || slide.scheduler.available === 'always') {
            return true;
          }

          const { type, timeToTime, daysTime, daysTimes, daysHourType, days } = slide.scheduler;

          const fromDate = timeToTime.from ? moment(timeToTime.from).toDate() : null;
          const toDate = timeToTime.to ? moment(timeToTime.to).toDate() : null;

          const isFromDateValid = fromDate ? moment(fromDate).isBefore() : true;
          const isToDateValid = toDate ? moment(toDate).isAfter() : true;

          const isInRangeOfDays = isFromDateValid && isToDateValid;

          if (!isInRangeOfDays || type === 'time-to-time') {
            return isInRangeOfDays;
          }

          const now = moment();
          const currentDay = DAYS_OF_THE_WEEK[now.isoWeekday() - 1];
          const midnight = now.clone().startOf('day');
          const currentMinute = now.diff(midnight, 'minutes');

          const isValidDay =
            (daysHourType === 'same-hours' && days.length === 0) || days.includes(currentDay);

          if (!isValidDay) {
            return false;
          }

          if (daysHourType === 'same-hours') {
            return currentMinute >= daysTime[0] && currentMinute <= daysTime[1];
          }

          const [startMinute, endMinute] = daysTimes[currentDay];

          return currentMinute >= startMinute && currentMinute <= endMinute;
        });

        const hasActivePriorityItems =
          tempFilteredSlides.filter((slide) => slide.display_priority === DISPLAY_PRIORITY.PRIORITY)
            .length > 0;

        const hasNonFallbackActiveItems =
          tempFilteredSlides.filter((slide) => slide.display_priority !== DISPLAY_PRIORITY.FALLBACK)
            .length > 0;

        if (!hasNonFallbackActiveItems) {
          this.filteredSlides = tempFilteredSlides.filter(
            (slide) => slide.display_priority === DISPLAY_PRIORITY.FALLBACK,
          );
        } else {
          this.filteredSlides = tempFilteredSlides.filter((slide) =>
            hasActivePriorityItems
              ? slide.display_priority === DISPLAY_PRIORITY.PRIORITY
              : slide.display_priority !== DISPLAY_PRIORITY.FALLBACK,
          );
        }
      },

      async fetchVideo(url) {
        try {
          const response = await fetch(url);
          const video = await response.blob();
          const videoUrl = URL.createObjectURL(video);

          return { url: videoUrl, file: video };
        } catch (error) {
          logger.addLog(`Error fetching Video (${url}): ${error}`);
          return null;
        }
      },

      async initPlaylist() {
        if (this.filteredSlides.length >= 2) {
          this.currentSlideIndex = 0;
          this.nextSlideIndex = 1;
        }

        await this.startSlider();
      },

      async startSlider() {
        if (this.filteredSlides.length === 0) {
          return await this.initTimer(1000);
        }

        const firstSlide = this.filteredSlides[0];

        await this.initTimer(firstSlide.display_timer * 1000);
      },

      async initTimer(after) {
        this.destroyTimer();
        const slideDuration = after > 1000 ? after : 1000;

        await new Promise((resolve, reject) => {
          this.time = setTimeout(async () => {
            try {
              await this.nextSlide();
              resolve();
            } catch (error) {
              logger.addLog('Error in nextSlide: ' + error);
              reject();
            } finally {
              this.destroyTimer();
            }
          }, slideDuration);
        });
      },

      destroyTimer() {
        if (this.time) {
          clearTimeout(this.time);
        }
      },

      async nextSlide() {
        if (this.filteredSlides.length < 2) {
          // Current slide will be the same one
          this.initTimer((this.currentSlide ? this.currentSlide.display_timer : 1) * 1000);
          return;
        }

        const newCurrentSlideIndex = (this.currentSlideIndex + 1) % this.filteredSlides.length;
        const newNextSlideIndex = (this.currentSlideIndex + 2) % this.filteredSlides.length;

        // Minimizing reactivity
        if (newCurrentSlideIndex !== this.currentSlideIndex) {
          this.currentSlideIndex = newCurrentSlideIndex;
        }
        if (newNextSlideIndex !== this.nextSlideIndex) {
          this.nextSlideIndex = newNextSlideIndex;
        }

        this.restartSignal = new Date().getTime().toString();

        // Current slide will be the next one (this.currentSlide depends on this.currentSlideIndex)
        await this.initTimer((this.currentSlide ? this.currentSlide.display_timer : 1) * 1000);
      },

      displaySlide(slide) {
        return this.currentSlide?.item_id === slide.item_id;
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/sass/animations.scss';

  .playlist-player {
    height: 100%;
    overflow: hidden;
  }

  .slideIn {
    transform: scaleX(1);
    transform-origin: right;
    transition: 0.5s transform ease-in, 0.5s visibility ease-in;
  }

  .slideOut {
    transform: scaleX(0);
    transform-origin: left;
    transition: 0.5s transform ease-in, 0.5s visibility ease-in;
  }

  .fadeIn,
  .fadeOut {
    -webkit-animation-duration: 1s;
    animation-duration: 1s;
    -webkit-animation-fill-mode: both;
    animation-fill-mode: both;
  }

  .fadeIn {
    -webkit-animation-name: fadeIn;
    animation-name: fadeIn;
  }

  .fadeOut {
    -webkit-animation-name: fadeOut;
    animation-name: fadeOut;
  }
</style>

<style lang="scss">
  .playlist-slide {
    position: absolute;
    overflow: hidden;
  }

  .playlist-slide-video .playlist-slide-audio {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .playlist-slide {
    width: 100%;
    height: 100%;

    .playlist-slide-image,
    .playlist-slide-video,
    .playlist-slide-audio,
    .playlist-slide-app-rss,
    .playlist-slide-app-table,
    .playlist-slide-trafiklab-table,
    .playlist-slide-weather-table,
    .playlist-slide-pdf,
    .player-slide-youtube {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .playlist-slide-video .playlist-slide-audio {
      &.stretch {
        object-fit: fill;
      }

      &.origin {
        object-fit: none;
      }
    }

    .player-slide-youtube {
      iframe {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        border: 0;
        width: 100%;
        height: 100%;
      }
    }
  }
</style>
